import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import type {
    ArchivosGeneradosHoy,
    ArchivosGeneradosHoyResponse
} from '../pages/GeneracionArchivosSRT/consts/types';

const RGRL_BASE_URL = 'vinculacion-rgrl';
const VIISITS_BASE_URL = 'visitas-rgrl';

export interface GenerateFilesBody {
    idsPresentacion: number[]
}

export interface GenerateVisitsFilesBody {
    idsVisita: number[]
}


export type SRT_RESPONSE_RGRL_TXT_TYPE = 'RB' | 'RC' | 'RH' | 'RI' | 'RM' | 'RO' | 'RS'
export type SRT_RESPONSE_VISITS_TXT_TYPE = 'VI' | 'RD'

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});
 
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const rgrlVinculationApi = createApi({
    reducerPath: 'rgrlVinculationApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'VincluationRGRLPresentations',
        'Contracts',
        'Establishments',
        'Visits',
        'GeneratedFilesToday'
    ],
    endpoints: (builder) => ({
        getRGRLVinculationPresentations: builder.query({
            query: (params) => {
                return {
                    url: `${RGRL_BASE_URL}/presentaciones`,
                    params: params
                };
            },
            providesTags: ['VincluationRGRLPresentations']
        }),
        getRGRLVinculationContracts: builder.query({
            query: ({ idOrganizacion }) => ({
                url: `${RGRL_BASE_URL}/contratos`,
                params: {
                    ...(idOrganizacion?.id
                        ? { idOrganizacion: idOrganizacion.id }
                        : idOrganizacion
                          ? { idOrganizacion }
                          : {})
                }
            }),
            providesTags: ['Contracts'],
            transformResponse: (response: any) => response?.data?.contratos
        }),
        getRGRLVinculationEstablishments: builder.query({
            query: (arg) => ({
                url: `${RGRL_BASE_URL}/establecimientos`,
                params: {
                    ...(arg?.idContrato ? { idOrganismoCentral: arg.idContrato?.id } : {})
                }
            }),
            providesTags: ['Establishments'],
            transformResponse: (response: any) => response?.data?.establecimientos
        }),
        generateFiles: builder.mutation({
            query: (body: GenerateFilesBody) => ({
                url: `${RGRL_BASE_URL}/archivos`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['VincluationRGRLPresentations', 'GeneratedFilesToday']
        }),
        // Contador de archivos SRT generados en el día (Visitas y RGRL/RRL), total y por cliente.
        getGeneratedFilesToday: builder.query<ArchivosGeneradosHoy, void>({
            query: () => ({
                url: `${RGRL_BASE_URL}/archivos/generados-hoy`
            }),
            providesTags: ['GeneratedFilesToday'],
            transformResponse: (response: ArchivosGeneradosHoyResponse) => response?.data
        }),
        uploadResponseSRT: builder.mutation({
            query: ({ nameType, body }: { nameType: SRT_RESPONSE_RGRL_TXT_TYPE; body: any }) => ({
                url: `${RGRL_BASE_URL}/archivos/${nameType}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['VincluationRGRLPresentations']
        }),
        exportRGRLPresentations: builder.query({
            query: (params) => ({
                url: `${RGRL_BASE_URL}/presentaciones/exportar`,
                params: params
            })
        }),
        //VISITS ENDPOINTS
        getVisitsSRT: builder.query({
            query: (params) => {
                return {
                    url: `${VIISITS_BASE_URL}`,
                    params: {
                        ...params,
                        ...(params?.idOrganizacion?.id
                            ? { idOrganizacion: params.idOrganizacion?.id }
                            : params?.idOrganizacion
                              ? { idOrganizacion: params.idOrganizacion }
                              : {})
                    }
                };
            },
            providesTags: ['Visits']
        }),
        generateVisitFilesSRT: builder.mutation({
            query: (body: GenerateVisitsFilesBody) => ({
                url: `${VIISITS_BASE_URL}/archivos`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Visits', 'GeneratedFilesToday']
        }),
        uploadVisitsResponseSRT: builder.mutation({
            query: ({ nameType, body }: { nameType: SRT_RESPONSE_VISITS_TXT_TYPE; body: any }) => ({
                url: `${VIISITS_BASE_URL}/archivos/${nameType}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Visits']
        }),
        exportVisitsSRT: builder.query<any, Record<string, any>>({
            query: (params) => ({
                url: `${VIISITS_BASE_URL}/exportar`,
                params
            })
        })
    })
});

export const {
    useGetRGRLVinculationPresentationsQuery,
    useGetRGRLVinculationContractsQuery,
    useGetRGRLVinculationEstablishmentsQuery,
    useGenerateFilesMutation,
    useGetGeneratedFilesTodayQuery,
    useUploadResponseSRTMutation,
    useLazyExportRGRLPresentationsQuery,
    //VISITS ENDPOINTS
    useGetVisitsSRTQuery,
    useGenerateVisitFilesSRTMutation,
    useUploadVisitsResponseSRTMutation,
    useLazyExportVisitsSRTQuery
} = rgrlVinculationApi;
